// src/pages/Dashboard.js
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ChartPieIcon, ChartBarIcon, CpuChipIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { isAdminOrSuperAdmin, getBearerHeaders } from '../lib/auth';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

// Componente reutilizável para os cards do dashboard
const DashboardCard = ({ title, icon, children }) => (
  <Card className="h-full">
    <CardHeader className="flex-row items-center gap-4 space-y-0">
      <div className="rounded-lg bg-primary/10 p-2 text-primary">
        {React.cloneElement(icon, { className: "h-6 w-6" })}
      </div>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent className="text-sm text-muted-foreground">{children}</CardContent>
  </Card>
);

// Componente para os cards de estatísticas (KPIs)
const StatCard = ({ title, value, description }) => (
  <Card>
    <CardContent className="space-y-2 p-6">
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="text-3xl font-semibold tracking-tight">{value}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [campanhas, setCampanhas] = useState([]);
  const [assistentes, setAssistentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clienteId = userData.clienteId || userData._id || userData.id;
  const isAdmin = isAdminOrSuperAdmin(userData);
  const baseUrl = API_BASE_URL || 'https://ia.discador.net';

  // Paleta de cores para os gráficos, alinhada com o tema
  const CHART_COLORS = {
    green: '#4caf50',
    red: '#f44336',
    gray: '#9e9e9e',
    blue: '#3b82f6',
    purple: '#8b5cf6',
  };

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      if (!clienteId && !isAdmin) {
        const toastId = toast.loading('Redirecionando...');
        toast.error('Cliente não identificado. Faça login novamente.', { id: toastId });
        window.location.href = '/';
        return;
      }
      const headers = getBearerHeaders();
      if (!headers) {
        const toastId = toast.loading('Redirecionando...');
        toast.error('Sessão expirada. Faça login novamente.', { id: toastId });
        window.location.href = '/';
        return;
      }

      const params = {};
      const tokenClient = localStorage.getItem('tokenClient');

      if (!isAdmin && clienteId) {
        params.clienteId = clienteId;
      }

      // Adicione o tokenClient aos parâmetros se ele existir
      if (tokenClient) {
        params.tokenClient = tokenClient;
      }

      // Faz requisição paralela para campanhas e assistentes (headers explícitos — evita perda no merge)
      const [campanhasRes, assistentesRes] = await Promise.all([
        axios.get(`${baseUrl}/api/campanhas`, { headers, params }),
        axios.get(`${baseUrl}/api/assistentes/master`, { headers, params })
      ]);

      setCampanhas(campanhasRes.data);
      setAssistentes(assistentesRes.data);
      setError('');

      // Log detalhado das campanhas com seus assistentes
      campanhasRes.data.forEach((campanha, index) => {
        // Verifica todos os campos possíveis que podem conter o ID do assistente
        const assistantFields = {
          'assistantId': campanha.assistantId,
          'assistenteSelecionado': campanha.assistenteSelecionado,
          'assistantId._id': campanha.assistantId?._id,
          'assistantId.vapiId': campanha.assistantId?.vapiId,
          'sip.vapiId': campanha.sip?.vapiId,
          'sip._id': campanha.sip?._id
        };

        // Tenta encontrar o assistente correspondente
        const assistantId = campanha.assistantId ||
          campanha.assistenteSelecionado ||
          campanha.assistantId?._id ||
          campanha.assistantId?.vapiId ||
          campanha.sip?.vapiId ||
          campanha.sip?._id;

        if (assistantId) {
          // Busca o assistente correspondente na lista
          const foundAssistant = assistentesRes.data.find(a =>
            (a._id === assistantId) ||
            (a.vapiId === assistantId) ||
            (a.assistantId && (a.assistantId._id === assistantId || a.assistantId.vapiId === assistantId))
          );
        } else {
          console.log('Nenhum ID de assistente encontrado para esta campanha');
        }
      });
    } catch (err) {
      console.error('Erro detalhado ao carregar dados:', err);
      if (err.response) {
        console.error('Resposta do servidor com erro:', err.response.data);
        console.error('Status do erro:', err.response.status);
      }
      setError('Não foi possível carregar os dados.');
    } finally {
      setLoading(false);
    }
  }, [clienteId, isAdmin, baseUrl]);


  // Função auxiliar para encontrar o nome de um assistente pelo ID
  const getAssistenteNomeById = (id) => {
    if (!id) return 'Assistente não definido';

    const assistente = assistentes.find(a =>
      (a._id === id) ||
      (a.vapiId === id) ||
      (a.assistantId && (a.assistantId._id === id || a.assistantId.vapiId === id))
    );

    return assistente
      ? assistente.nome || assistente.name || 'Assistente sem nome'
      : `Assistente não encontrado (ID: ${id})`;
  };

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Processamento de dados para os gráficos
  const statusData = React.useMemo(() => {
    const totais = { ativa: 0, cancelada: 0, falhou: 0, em_fila: 0 };
    campanhas.forEach(c => {
      totais[c.status] = (totais[c.status] || 0) + 1;
    });

    const result = Object.entries(totais)
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: value,
        color: {
          'Em_fila': CHART_COLORS.gray,
          'Ativa': CHART_COLORS.blue,
          'Atendido': CHART_COLORS.green,
          'Reenviada': '#f97316', // Cor laranja para reenviada
          'Cancelada': CHART_COLORS.gray,
          'Falhou': CHART_COLORS.red,
          // Status antigos (compatibilidade)
          'Pendente': CHART_COLORS.gray,
          'Concluída': CHART_COLORS.green,
          'Concluida': CHART_COLORS.green,
          'Sucesso': CHART_COLORS.green,
          'Erro': CHART_COLORS.red,
          'Em_progresso': CHART_COLORS.blue,
          'Em_andamento': CHART_COLORS.purple,
          'Enviado': CHART_COLORS.blue
        }[name.charAt(0).toUpperCase() + name.slice(1)] || CHART_COLORS.purple
      }))
      .filter(item => item.value > 0);

    return result;
  }, [campanhas]);

  const topCampaignsData = React.useMemo(() => {
    const result = campanhas
      .map(c => ({
        nome: c.nome || `Campanha ${c._id.slice(-4)}`,
        sucesso: c.resultado
          ? Object.values(c.resultado).filter(r => r.status === 'atendido').length
          : 0,
      }))
      .sort((a, b) => b.sucesso - a.sucesso)
      .slice(0, 5);

    return result;
  }, [campanhas]);

  const topAssistantsData = React.useMemo(() => {

    const assistantUsage = {};

    campanhas.forEach(c => {
      // Tenta encontrar o ID do assistente em diferentes campos possíveis
      const assistantId = c.assistantId ||
        c.assistenteSelecionado ||
        c.assistantId?._id ||
        c.assistantId?.vapiId ||
        c.sip?.vapiId ||
        c.sip?._id || c.sip?.phoneNumberBody?.assistantId;

      if (!assistantId) {
        return;
      }


      if (!assistantUsage[assistantId]) {
        assistantUsage[assistantId] = {
          count: 0,
          sucesso: 0,
          nome: getAssistenteNomeById(assistantId)
        };
      }

      assistantUsage[assistantId].count += 1;

      // Conta chamadas bem-sucedidas para este assistente
      const successCount = c.resultado
        ? Object.values(c.resultado).filter(r => r.status === 'concluída').length
        : 0;
      assistantUsage[assistantId].sucesso += successCount;
    });


    // Converte para array e ordena
    const sortedAssistants = Object.entries(assistantUsage)
      .map(([id, data]) => ({
        id,
        nome: data.nome,
        count: data.count,
        sucesso: data.sucesso
      }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return b.sucesso - a.sucesso;
      })
      .slice(0, 5)
      .map(item => ({
        nome: item.nome,
        uso: item.count,
        sucesso: item.sucesso
      }));

    return sortedAssistants;
  }, [campanhas, assistentes]);

  const topAssistantsBySuccessData = React.useMemo(() => {

    const assistantSuccess = {};

    campanhas.forEach(c => {
      // Tenta encontrar o ID do assistente em diferentes campos possíveis
      const assistantId = c.assistantId ||
        c.assistenteSelecionado ||
        c.assistantId?._id ||
        c.assistantId?.vapiId ||
        c.sip?.vapiId ||
        c.sip?._id || c.sip?.phoneNumberBody?.assistantId;

      if (!assistantId) {
        return;
      }

      if (!assistantSuccess[assistantId]) {
        assistantSuccess[assistantId] = {
          sucesso: 0,
          nome: getAssistenteNomeById(assistantId)
        };
      }

      // Conta chamadas bem-sucedidas para este assistente
      const successCount = c.resultado
        ? Object.values(c.resultado).filter(r => r.status === 'concluída').length
        : 0;
      assistantSuccess[assistantId].sucesso += successCount;
    });


    // Converte para array e ordena
    const sortedAssistants = Object.entries(assistantSuccess)
      .map(([id, data]) => ({
        id,
        nome: data.nome,
        sucesso: data.sucesso
      }))
      .sort((a, b) => b.sucesso - a.sucesso)
      .slice(0, 5)
      .map(item => ({
        nome: item.nome,
        sucesso: item.sucesso
      }));

    return sortedAssistants;
  }, [campanhas, assistentes]);

  const totalCalls = React.useMemo(() =>
    campanhas.reduce((acc, c) => acc + (c.contatos?.length || 0), 0),
    [campanhas]);

  const successfulCalls = React.useMemo(() =>
    campanhas.reduce((acc, c) =>
      acc + (c.resultado
        ? Object.values(c.resultado).filter(r => r.status === 'concluída').length
        : 0), 0),
    [campanhas]);

  if (loading) {
    return <div className="p-8 text-white text-center">Carregando dados do dashboard...</div>;
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="bg-red-800 text-red-200 p-4 rounded-lg inline-flex items-center">
          <ExclamationTriangleIcon className="h-6 w-6 mr-3" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-900 text-white min-h-full">
      {/* Título */}
      <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

      {/* Grid de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total de Campanhas"
          value={campanhas.length}
          description="Número total de campanhas criadas"
        />
        <StatCard
          title="Total de Ligações"
          value={totalCalls}
          description="Soma de todos os contatos"
        />
        <StatCard
          title="Ligações de Sucesso"
          value={successfulCalls}
          description="Chamadas concluídas com sucesso"
        />
        <StatCard
          title="Taxa de Sucesso"
          value={totalCalls > 0 ? `${((successfulCalls / totalCalls) * 100).toFixed(1)}%` : 'N/A'}
          description="Percentual de sucesso"
        />
      </div>

      {/* Se não houver campanhas */}
      {campanhas.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-xl text-gray-400 mb-4">Nenhuma campanha encontrada</p>
          <button
            onClick={() => window.location.href = '/campanhas/nova'}
            className="px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            Criar Primeira Campanha
          </button>
        </div>
      ) : (
        // Grid dos Gráficos
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Gráfico de Pizza (ocupa 2 colunas) */}
          <div className="lg:col-span-2">
            <DashboardCard title="Distribuição de Status" icon={<ChartPieIcon />}>
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={100}
                      fill="#8884d8"
                      label
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#111827',
                        color: '#f9fafb',
                        border: '1px solid #374151',
                        borderRadius: '0.5rem'
                      }}
                      itemStyle={{ color: '#f9fafb' }}
                      labelStyle={{ color: '#f3f4f6' }}
                    />
                    <Legend iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-gray-400">Sem dados de status para exibir</p>
                </div>
              )}
            </DashboardCard>
          </div>

          {/* Gráfico de Barras (ocupa 3 colunas) */}
          <div className="lg:col-span-3">
            <DashboardCard title="Top 5 Campanhas por Sucesso" icon={<ChartBarIcon />}>
              {topCampaignsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={topCampaignsData}
                    layout="vertical"
                    margin={{ left: 80, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
                    <XAxis type="number" stroke="#9ca3af" />
                    <YAxis
                      type="category"
                      dataKey="nome"
                      stroke="#9ca3af"
                      width={100}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(139, 92, 246, 0.1)' }}
                      contentStyle={{
                        backgroundColor: '#111827',
                        color: '#f9fafb',
                        border: '1px solid #374151',
                        borderRadius: '0.5rem'
                      }}
                      labelStyle={{ color: '#f3f4f6' }}
                      formatter={(value) => ([
                        <span style={{ color: '#ffffff' }}>{value}</span>,
                        <span style={{ color: '#22c55e' }}>sucesso</span>
                      ])}
                    />
                    <Bar dataKey="sucesso" fill={CHART_COLORS.purple} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-gray-400">Sem campanhas com chamadas de sucesso</p>
                </div>
              )}
            </DashboardCard>
          </div>

          {/* Primeiro Gráfico de Assistentes (Top por Uso) */}
          <div className="lg:col-span-2">
            <DashboardCard title="Top 5 Assistentes Mais Usados" icon={<CpuChipIcon />}>
              {topAssistantsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={topAssistantsData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
                    <XAxis dataKey="nome" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip cursor={{ fill: 'rgba(139, 92, 246, 0.1)' }} contentStyle={{
                      backgroundColor: '#1f2937',
                      color: '#e5e7eb',
                      border: '1px solid #4b5563',
                      borderRadius: '0.5rem'
                    }} />
                    <Bar dataKey="uso" fill="#3b82f6" name="Nº de Campanhas" />
                    <Bar dataKey="sucesso" fill="#4caf50" name="Chamadas de Sucesso" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-gray-400">Nenhum assistente encontrado para exibir</p>
                </div>
              )}
            </DashboardCard>
          </div>

          {/* Segundo Gráfico de Assistentes (Top por Sucesso) */}
          <div className="lg:col-span-3">
            <DashboardCard title="Top 5 Assistentes por Chamadas de Sucesso" icon={<CpuChipIcon />}>
              {topAssistantsBySuccessData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={topAssistantsBySuccessData}
                    layout="vertical"
                    margin={{ left: 80, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
                    <XAxis type="number" stroke="#9ca3af" />
                    <YAxis
                      type="category"
                      dataKey="nome"
                      stroke="#9ca3af"
                      width={100}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip cursor={{ fill: 'rgba(139, 92, 246, 0.1)' }} contentStyle={{
                      backgroundColor: '#1f2937',
                      color: '#e5e7eb',
                      border: '1px solid #4b5563',
                      borderRadius: '0.5rem'
                    }} />
                    <Bar dataKey="sucesso" fill="#4caf50" name="Chamadas de Sucesso" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-gray-400">Nenhum assistente com chamadas de sucesso</p>
                </div>
              )}
            </DashboardCard>
          </div>
        </div>
      )}
    </div>
  );
}
